import { Dialog, Flex, Text, TextArea, Button, ScrollArea } from '@radix-ui/themes'
import { useState, useRef } from 'react'
import type { Restaurant } from '@ablunch/map-service'
import ExifReader from 'exifreader'

interface RecordFormProperties {
  restaurant: Restaurant
  open: boolean
  onClose: () => void
  onSubmit: (data: { restaurant: Restaurant; content: string; images: string[]; photoTakenAt?: string }) => Promise<void>
}

export const RecordForm = ({ restaurant, open, onClose, onSubmit }: RecordFormProperties) => {
  const [content, setContent] = useState('')
  const [images, setImages] = useState<string[]>([])
  const [photoTakenAt, setPhotoTakenAt] = useState<string | undefined>(undefined)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const fileInputReference = useRef<HTMLInputElement>(null)
  const cameraInputReference = useRef<HTMLInputElement>(null)

  const handleImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files) return

    const imagePromises = Array.from(files).map((file) => {
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          if (e.target?.result) {
            resolve(e.target.result as string)
          }
        }
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    })

    try {
      const base64Images = await Promise.all(imagePromises)
      setImages((previous) => [...previous, ...base64Images])

      // 첫 번째 이미지에서 EXIF 날짜 추출
      if (files.length > 0 && !photoTakenAt) {
        try {
          const tags = await ExifReader.load(files[0])
          const dateTime = tags['DateTimeOriginal']?.description || tags['DateTime']?.description
          if (dateTime) {
            setPhotoTakenAt(dateTime)
            console.log('EXIF 촬영 날짜:', dateTime)
          }
        } catch (error) {
          console.log('EXIF 데이터 없음 또는 읽기 실패:', error)
        }
      }
    } catch (error) {
      console.error('이미지 로드 실패:', error)
      alert('이미지를 불러오는데 실패했습니다.')
    }
  }

  const handleRemoveImage = (index: number) => {
    setImages((previous) => previous.filter((_, i) => i !== index))
  }

  const handleSubmit = async () => {
    if (!content.trim()) {
      alert('내용을 입력해주세요.')
      return
    }

    setIsSubmitting(true)
    try {
      await onSubmit({ restaurant, content, images, photoTakenAt })
      setContent('')
      setImages([])
      setPhotoTakenAt(undefined)
      onClose()
    } catch (error) {
      console.error('기록 저장 실패:', error)
      alert('기록 저장에 실패했습니다.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onClose}>
      <Dialog.Content style={{ maxWidth: '500px', maxHeight: '90vh' }}>
        <Dialog.Title>음식 기록하기</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          {restaurant.name}
        </Dialog.Description>

        <ScrollArea>
          <Flex direction="column" gap="4">
            <Flex direction="column" gap="2">
              <Text size="2" weight="bold">
                사진
              </Text>
              <Flex gap="2" wrap="wrap">
                {images.map((image, index) => (
                  <div
                    key={index}
                    style={{
                      position: 'relative',
                      width: '100px',
                      height: '100px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={image}
                      alt={`preview ${index}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <button
                      onClick={() => handleRemoveImage(index)}
                      style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        background: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </Flex>
              <Flex gap="2">
                <Button
                  type="button"
                  variant="soft"
                  onClick={() => cameraInputReference.current?.click()}
                >
                  📷 카메라
                </Button>
                <Button
                  type="button"
                  variant="soft"
                  onClick={() => fileInputReference.current?.click()}
                >
                  🖼️ 갤러리
                </Button>
              </Flex>
              <input
                ref={cameraInputReference}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={handleImageSelect}
                style={{ display: 'none' }}
              />
              <input
                ref={fileInputReference}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageSelect}
                style={{ display: 'none' }}
              />
              {photoTakenAt && (
                <Text size="1" color="gray">
                  📸 촬영 시각: {photoTakenAt.replace(':', '년 ').replace(':', '월 ').replace(' ', '일 ')}
                </Text>
              )}
            </Flex>

            <Flex direction="column" gap="2">
              <Text size="2" weight="bold">
                메모
              </Text>
              <TextArea
                placeholder="오늘 먹은 음식과 느낌을 기록해보세요..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={6}
              />
            </Flex>
          </Flex>
        </ScrollArea>

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray">
              취소
            </Button>
          </Dialog.Close>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? '저장 중...' : '기록하기'}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}
